import { useCommands } from "@components/commands/useCommands";
import { useInlineNewVersionNotice } from "@components/newVersion/useInlineNewVersionNotice";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { SideNav } from "@design-system/SideNav/SideNav";
import { SideNavHeader } from "@design-system/SideNav/SideNavHeader";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { useSideNavMode } from "@design-system/SideNav/SideNavProvider";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import { useCanManageOrganizationTaskTemplates } from "@hooks/permissions/organization/useCanManageOrganizationTaskTemplates";
import { useCanViewOrganizationActivityLog } from "@hooks/permissions/organization/useCanViewOrganizationActivityLog";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    CalendarIcon,
    ChartOfAccountsIcon,
    ClockIcon,
    GrowthIcon,
    HomeIcon,
    PlusIcon,
    ServicesIcon,
    SkillsIcon,
    TasksIcon,
} from "@icons";
import { useOrganizationTodoTasksCount } from "@api/organizationTask.api";
import { OrgNavigationShortcutRegistrar } from "../OrgNavigationShortcutRegistrar";
import { useOrgNavigationShortcutKeys } from "../useResolvedOrgNavigationShortcuts";
import { ActionsRow } from "./ActionsRow";
import { FirmSection } from "./FirmSection";
import { InviteMembersButton } from "./InviteMembersButton";
import { OrgHelpMenu } from "./levelHelpMenus";
import { OrgClientsSection } from "./OrgClientsSection";
import { ORG_SIDE_NAV_SECTIONS } from "./orgSideNavSections";
import { OrgSideNavPicker } from "./OrgSideNavPicker";
import { useIsClientRowActive } from "./useClientRowTarget";
import { useRecentClients } from "./useRecentClients";
import { useSideNavSections } from "./useSideNavSections";

/**
 * The org sidebar on `SideNav`, mapped from today's `OrganizationViewNavigation`.
 * Sections are Clients, Templates and Firm; the footer holds `?` and, for
 * users who can invite, Invite members (swapped for the new-version notice
 * while it shows).
 */
export const OrgSideNav = () => {
    const { open: openCommands } = useCommands();
    const canManageTaskTemplates = useCanManageOrganizationTaskTemplates();
    const canViewActivityLog = useCanViewOrganizationActivityLog();
    const {
        closeManagement: isCloseManagementEnabled,
        reportTemplates: isReportTemplatesEnabled,
        agentRuns: isAgentRunsEnabled,
        organizationActivityLog: isActivityLogEnabled,
    } = useTypedFlags();
    const isActionsRowVisible = isCloseManagementEnabled;
    const { data: todoTasksCount } = useOrganizationTodoTasksCount({
        enabled: isActionsRowVisible,
    });
    const { sectionProps } = useSideNavSections({
        level: "org",
        sections: ORG_SIDE_NAV_SECTIONS,
    });
    const recentClients = useRecentClients();
    const clientsSection = sectionProps("Clients");
    const isMini = useSideNavMode() === "mini";
    const isClientRowActive = useIsClientRowActive();
    const isFocusedClientRowShown =
        (isMini || clientsSection.isOpen) &&
        recentClients.some(({ id }) => isClientRowActive(id));
    const newVersionNotice = useInlineNewVersionNotice();
    const shortcutKeys = useOrgNavigationShortcutKeys();

    return (
        <>
            <OrgNavigationShortcutRegistrar />
            <SideNav
                aria-label="Main"
                header={<SideNavHeader picker={<OrgSideNavPicker />} />}
                top={
                    <>
                        <SideNavItem
                            onClick={() => openCommands()}
                            icon={<PlusIcon />}
                            label="New"
                            shortcut="mod+k"
                            trailing={<Shortcut keys="mod+k" size="sm" />}
                            trailingVisibility="reveal"
                        />
                        {isCloseManagementEnabled && (
                            <SideNavItem
                                to="/clients-work"
                                icon={<HomeIcon />}
                                label="Home"
                                shortcut={shortcutKeys("work")}
                            />
                        )}
                        {isActionsRowVisible && (
                            <ActionsRow
                                to="/clients-tasks"
                                count={todoTasksCount}
                                shortcut={shortcutKeys("tasks")}
                                isActive={
                                    isFocusedClientRowShown ? false : undefined
                                }
                            />
                        )}
                        {isActivityLogEnabled && canViewActivityLog && (
                            <SideNavItem
                                to="/organization-activity"
                                icon={<ClockIcon />}
                                label="Activity"
                                shortcut={shortcutKeys("activity")}
                            />
                        )}
                    </>
                }
                footer={
                    <>
                        <OrgHelpMenu />
                        {newVersionNotice ?? <InviteMembersButton />}
                    </>
                }
            >
                <OrgClientsSection
                    section={clientsSection}
                    recentClients={recentClients}
                    allClientsShortcut={shortcutKeys("clients")}
                />
                <SideNavSection {...sectionProps("Templates")}>
                    <SideNavItem
                        to="/templates/chart-of-accounts"
                        icon={<ChartOfAccountsIcon />}
                        label="Chart of Accounts"
                        shortcut={shortcutKeys("chart-of-accounts-templates")}
                    />
                    {isReportTemplatesEnabled && (
                        <SideNavItem
                            to="/templates/reports"
                            icon={<GrowthIcon />}
                            label="Reports"
                            shortcut={shortcutKeys("report-templates")}
                        />
                    )}
                    {isCloseManagementEnabled && (
                        <>
                            <SideNavItem
                                to="/templates/services"
                                icon={<ServicesIcon />}
                                label="Services"
                                shortcut={shortcutKeys("services")}
                            />
                            <SideNavItem
                                to="/templates/schedules"
                                icon={<CalendarIcon />}
                                label="Schedules"
                                shortcut={shortcutKeys("schedules")}
                            />
                            {canManageTaskTemplates && (
                                <SideNavItem
                                    to="/templates/tasks"
                                    icon={<TasksIcon />}
                                    label="Tasks"
                                />
                            )}
                        </>
                    )}
                </SideNavSection>
                {isAgentRunsEnabled && (
                    <SideNavSection {...sectionProps("Automation")}>
                        <SideNavItem
                            to="/automation/skills"
                            icon={<SkillsIcon />}
                            label="Skills"
                        />
                    </SideNavSection>
                )}
                <FirmSection section={sectionProps("Firm")} />
            </SideNav>
        </>
    );
};
